import { BillingStatus, BrandStatus } from '@innovationdepartment/proxima-sdk-axios';
import { Button, colors, DatePickerCalendar, Text } from '@innovationdepartment/proxima-ui';
import * as date from 'date-fns';
import type { BrandOverviewProps } from 'types/brandOverview';

type BrandActionProps = {
  onLockUnlockClick: () => void;
  onTrialExtend: (date: Date) => void;
};

type Section = {
  label: React.ReactNode;
  content: React.ReactNode;
};

export const getBrandActionSections = (
  props: BrandOverviewProps,
  actions: BrandActionProps
): Section[] => {
  const { onLockUnlockClick, onTrialExtend } = actions;
  const { brand, billing } = props;

  const isTrial = billing?.status === BillingStatus.Trial;
  const isCancelled = billing?.status === BillingStatus.Cancelled;
  const trialHasEnded = date.isAfter(new Date(), new Date(billing?.trialEnd || 0));

  const isLocked = brand.status === BrandStatus.Locked;

  const lockUnlockButtonProps = isLocked
    ? {
        label: 'Unlock brand',
        overrideColors: {
          backgroundColor: colors.proximaGreen50
        }
      }
    : {
        label: 'Lock brand',
        overrideColors: {
          backgroundColor: colors.red60
        }
      };

  const lockUnlockSection: Section = {
    label: `${isLocked ? 'Unlock' : 'Lock'} brand`,
    content: <Button {...lockUnlockButtonProps} onClick={onLockUnlockClick} />
  };

  const sections: Section[] = [lockUnlockSection];

  if (isTrial || isCancelled) {
    if (trialHasEnded) {
      const trialEndedSection: Section = {
        label: 'Trial ended on',
        content: (
          <Text variant="body2Semibold">
            {date.format(new Date(billing?.trialEnd || 0), 'MMMM dd, yyyy')}
          </Text>
        )
      };

      sections.push(trialEndedSection);
    } else {
      const trialEndSection: Section = {
        label: 'Trial ends on',
        content: (
          <Text variant="body2Semibold">
            {date.format(new Date(billing?.trialEnd || 0), 'MMMM dd, yyyy')}
          </Text>
        )
      };

      sections.push(trialEndSection);
    }

    const { trialEnd } = billing;
    const max90Days = date.addDays(new Date(), 90);
    const tomorrow = date.addDays(new Date(), 1);
    const calendarDate = trialHasEnded ? new Date() : new Date(trialEnd || 0);

    const trialExtensionSection: Section = {
      label: 'Extend trial',
      content: (
        <DatePickerCalendar
          key={calendarDate.toISOString()}
          date={calendarDate}
          onSelect={onTrialExtend}
          minDate={tomorrow}
          maxDate={max90Days}
        />
      )
    };

    sections.push(trialExtensionSection);
  }

  return sections;
};
